/**
 * 文件作用：任务状态管理（全局 store 实现）
 * - 将原先基于 useState 的局部 Hook 替换为 zustand 全局 store，保证不同组件/Hook 访问到的是同一份任务数据
 * - 提供任务列表的增删改查功能以及任务选择状态
 */

import { createWithEqualityFn } from 'zustand/traditional';
import type { GenerationTaskPayload, GenerationTaskResult } from '../types/generation';
import { loadSyncTasksCache, saveSyncTaskToCache } from '../utils/taskSyncCache';
import { restoreInlineMedia, hasMediaCacheRefs } from '../utils/taskMediaCache';


const generateTaskId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/** 内存中保留的最大任务数量，防止长时间使用导致内存膨胀 */
const MAX_TASKS_IN_MEMORY = 50;

/**
 * 任务状态枚举
 */
export enum TaskStatus {
  SUBMITTING = 'submitting', // 任务提交中（前端乐观占位）
  PENDING = 'pending',     // 已提交，等待开始
  RUNNING = 'running',     // 进行中
  SUCCESS = 'success',     // 成功
  FAILED = 'failed'        // 失败
}

/**
 * 任务数据结构
 */
export interface Task {
  id: string;              // 任务唯一标识
  requestId?: string;      // 请求ID
  statusUrl?: string;      // 状态查询URL
  status: TaskStatus;      // 任务状态
  model: string;           // 模型名称 (nano-banana, veo等)
  prompt?: string;         // 用户输入的提示词
  payload?: GenerationTaskPayload; // 提交的载荷
  createdAt: number;       // 创建时间戳
  updatedAt: number;       // 更新时间戳
  submittedAt?: number;    // 提交时间戳
  startedAt?: number;      // 开始时间戳
  finishedAt?: number;     // 完成时间戳
  result?: GenerationTaskResult;    // 任务结果
  error?: string;          // 错误信息
  progress?: number;       // 进度百分比 (0-100)
  metadata?: {
    previewNodeId?: string;
    executionId?: string;
    nodeGraphId?: string;
  };
}

/**
 * 任务管理 Store 的接口
 */
export interface UseTaskManagerReturn {
  tasks: Task[];                           // 任务列表
  _hydrated: boolean;                      // 是否已从 localStorage 恢复缓存
  addTask: (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => string;  // 添加任务
  updateTask: (id: string, updates: Partial<Task>) => void;  // 更新任务
  getTask: (id: string) => Task | undefined;  // 获取单个任务
  hydrateFromCache: () => void;            // 客户端挂载后从 localStorage 恢复缓存
}

/**
 * 使用 zustand 创建全局任务管理 store
 * 这样 useGeneration、CreatePage 以及其他组件调用 useTaskManager() 时会共享同一份任务数据
 *
 * 注意：初始化时 tasks 为空数组，客户端挂载后需调用 hydrateFromCache() 从 localStorage 恢复缓存
 * 这是因为 SSR 时 localStorage 不可用，直接在初始化时调用 loadSyncTasksCache() 会导致 hydration 不一致
 */
export const useTaskManager = createWithEqualityFn<UseTaskManagerReturn>()((set, get) => ({
  tasks: [], // SSR 安全：初始为空，客户端挂载后通过 hydrateFromCache 恢复
  _hydrated: false,

  hydrateFromCache: () => {
    // 防止重复 hydration
    if (get()._hydrated) {
      return;
    }
    const cached = loadSyncTasksCache();
    if (cached && cached.length > 0) {
      set((state) => {
        // 合并缓存任务与当前内存中的任务（去重）
        const existingIds = new Set(state.tasks.map((t) => t.id));
        const newTasks = cached.filter((t) => !existingIds.has(t.id));
        const merged = [...state.tasks, ...newTasks];
        // 按 createdAt 降序排列
        merged.sort((a, b) => b.createdAt - a.createdAt);
        return { tasks: merged.slice(0, MAX_TASKS_IN_MEMORY), _hydrated: true };
      });

      // 异步恢复 inline 媒体数据（从 IndexedDB）
      // 这样可以更快地显示任务列表，然后在后台恢复媒体数据
      void (async () => {
        const tasksToRestore = get().tasks.filter((t) => hasMediaCacheRefs(t.result));
        if (tasksToRestore.length === 0) return;

        const restoredTasks: Task[] = [];
        for (const task of tasksToRestore) {
          try {
            const restoredResult = await restoreInlineMedia(task.result);
            if (restoredResult !== task.result) {
              restoredTasks.push({ ...task, result: restoredResult as GenerationTaskResult });
            }
          } catch (error) {
            console.warn('[useTaskManager] 恢复任务媒体数据失败', task.id, error);
          }
        }

        if (restoredTasks.length > 0) {
          set((state) => ({
            tasks: state.tasks.map((t) => {
              const restored = restoredTasks.find((r) => r.id === t.id);
              return restored ?? t;
            }),
          }));
        }
      })();
    } else {
      set({ _hydrated: true });
    }
  },

  addTask: (task) => {
    // 每次调用都创建独立任务，不再基于 model + prompt 去重
    // 原因：同步任务（如 gemini 视频理解）每次提交都是独立的，去重会导致后者覆盖前者
    const id = generateTaskId();
    const now = Date.now();
    const newTask: Task = {
      ...task,
      id,
      createdAt: now,
      updatedAt: now,
      submittedAt: task.submittedAt ?? now,
    };
    set((state) => {
      const newTasks = [newTask, ...state.tasks];
      // 限制内存中的任务数量，移除最旧的已完成任务
      if (newTasks.length > MAX_TASKS_IN_MEMORY) {
        // 优先移除已完成/失败的任务
        const trimmed = newTasks.filter(
          (t, idx) => idx < MAX_TASKS_IN_MEMORY ||
            (t.status !== TaskStatus.SUCCESS && t.status !== TaskStatus.FAILED)
        ).slice(0, MAX_TASKS_IN_MEMORY + 10); // 保留少量进行中的任务
        return { tasks: trimmed };
      }
      return { tasks: newTasks };
    });
    return id;
  },

  updateTask: (id, updates) => {
    let taskToCache: Task | null = null;
    set((state) => ({
      tasks: state.tasks.map((t) => {
        if (t.id !== id) {
          return t;
        }
        const updatedTask: Task = { ...t, ...updates, updatedAt: Date.now() };
        if (
          (updatedTask.status === TaskStatus.SUCCESS || updatedTask.status === TaskStatus.FAILED) &&
          !updatedTask.requestId
        ) {
          taskToCache = updatedTask; // 终态且无 requestId 的同步任务写入缓存
        }
        return updatedTask;
      }),
    }));
    if (taskToCache) {
      // saveSyncTaskToCache 现在是异步的，使用 void 处理 Promise
      void saveSyncTaskToCache(taskToCache);
    }
  },

  getTask: (id) => {
    const s = get();
    return s.tasks.find((t) => t.id === id);
  },
}));

